import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import ResourcePicker from '../components/ResourcePicker';
import ControlledDraftJson from '../components/ControlledDraftJson';
import ProcedureAuthoringMetadata from '../components/ProcedureAuthoringMetadata';
import ProcedureRevisionBar from '../components/ProcedureRevisionBar';
import PinnedReferences from '../components/PinnedReferences';
import StationExecutionSettings, { FlowExecutionPolicy } from '../components/StationExecutionSettings';

const API_BASE = process.env.REACT_APP_API_URL;

// ── Constants ─────────────────────────────────────────────────────────────────

const STATION_TYPES = [
  'Raw Material', 'Assembly', 'Firmware Flash', 'Functional Test',
  'Burn-In', 'Visual Inspection', 'Quality Gate', 'Semi-Finished Goods',
  'Packaging', 'Rework', 'Hold', 'Release / Shipping', 'Finished Goods', 'Generic',
];

const TYPE_COLORS = {
  'Raw Material':        '#a78bfa',
  'Assembly':            '#60a5fa',
  'Firmware Flash':      '#34d399',
  'Functional Test':     '#46BB89',
  'Burn-In':             '#f59e0b',
  'Visual Inspection':   '#38bdf8',
  'Quality Gate':        '#EC2124',
  'Semi-Finished Goods': '#94a3b8',
  'Packaging':           '#fb923c',
  'Rework':              '#ef4444',
  'Hold':                '#dc2626',
  'Release / Shipping':  '#10b981',
  'Finished Goods':      '#22c55e',
  'Generic':             '#64748b',
};

const STATUS_COLORS = {
  Draft: '#94a3b8', 'In Review': '#60a5fa', Released: '#46BB89',
  Superseded: '#f59e0b', Obsolete: '#EC2124',
};

// Control-plan (IATF 16949 / APQP) enums — kept in sync with backend ProcessFlow model.
const CONTROL_CHAR_TYPES = ['Product', 'Process'];
const CONTROL_CLASSIFICATIONS = ['Standard', 'Significant', 'Critical', 'Special'];
const CONTROL_PLAN_TYPES = ['', 'Prototype', 'Pre-launch', 'Production'];
const CLASS_COLORS = { Standard: '#94a3b8', Significant: '#60a5fa', Critical: '#EC2124', Special: '#f59e0b' };

const CELL_PX = 80; // pixels per grid cell

const BOM_ACCENT = '#f472b6';

// ── Helpers ───────────────────────────────────────────────────────────────────

// A BOM item is "used" iff some station.parts entry has partNumber === item.ninPartNumber.
function computeUnusedBomItems(bomItems, stations) {
  const usedNumbers = new Set();
  (stations || []).forEach(st => {
    (st.parts || []).forEach(p => {
      if (p.partNumber) usedNumbers.add(p.partNumber);
    });
  });
  return (bomItems || []).filter(item => !usedNumbers.has(item.ninPartNumber));
}

function normalizeRole(role) {
  if (!role) return '';
  if (Array.isArray(role)) return (role[0]||'').toLowerCase().trim();
  return String(role).toLowerCase().trim();
}
function genId(stations) {
  const nums = stations.map(s => parseInt((s.stationId||'S000').replace(/\D/g,''), 10)).filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `S${String(next).padStart(3,'0')}`;
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#666';
  return (
    <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20,
      fontSize:11, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase',
      background: color+'22', color }}>
      {status}
    </span>
  );
}

// ── Station block (on grid) ───────────────────────────────────────────────────

function StationBlock({ station, selected, canEdit, onSelect, onDragStart }) {
  const color = TYPE_COLORS[station.type] || '#64748b';
  return (
    <div
      draggable={canEdit}
      onDragStart={canEdit ? (e) => onDragStart(e, station.stationId) : undefined}
      onClick={() => onSelect(station.stationId)}
      style={{
        gridColumn: `${station.gridX + 1} / span ${station.gridW || 1}`,
        gridRow:    `${station.gridY + 1} / span ${station.gridH || 1}`,
        background: selected ? color + '33' : color + '18',
        border: `2px solid ${selected ? color : color + '55'}`,
        borderRadius: 6,
        padding: '6px 8px',
        cursor: canEdit ? 'grab' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'border-color 0.1s, background 0.1s',
        boxSizing: 'border-box',
        minHeight: 0,
        zIndex: selected ? 2 : 1,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: color, textTransform: 'uppercase',
        letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {station.type}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ni-text)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {station.name || '(unnamed)'}
      </div>
      <div style={{ fontSize: 10, color: 'var(--ni-text-muted)', fontFamily: 'monospace' }}>
        {station.stationId}
      </div>
    </div>
  );
}

// ── Station palette entry ─────────────────────────────────────────────────────

function PaletteItem({ type, onAdd }) {
  const color = TYPE_COLORS[type] || '#64748b';
  return (
    <div onClick={() => onAdd(type)}
      style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px',
        borderRadius:6, cursor:'pointer', border: `1px solid ${color}33`,
        background: color+'11', transition:'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background = color+'22'}
      onMouseLeave={e => e.currentTarget.style.background = color+'11'}
    >
      <div style={{ width:10, height:10, borderRadius:3, background: color, flexShrink:0 }} />
      <span style={{ fontSize:12, color:'var(--ni-text)' }}>{type}</span>
    </div>
  );
}

// ── Dependency arrows SVG overlay ────────────────────────────────────────────

const CELL_TOTAL = CELL_PX + 2; // 80 + gap(2)

function stationCenter(s) {
  return {
    x: (s.gridX + (s.gridW || 1) / 2) * CELL_TOTAL - 1,
    y: (s.gridY + (s.gridH || 1) / 2) * CELL_TOTAL - 1,
  };
}

function ArrowOverlay({ stations, gridW, gridH }) {
  const stationMap = Object.fromEntries(stations.map(s => [s.stationId, s]));
  const arrows = [];

  stations.forEach(dest => {
    (dest.dependencies || []).forEach(dep => {
      const src = stationMap[dep.fromStationId];
      if (!src) return;
      const { x: x1, y: y1 } = stationCenter(src);
      const { x: x2, y: y2 } = stationCenter(dest);
      const dx = x2 - x1, dy = y2 - y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 4) return;
      const ux = dx / len, uy = dy / len;
      const pad = 28;
      const sx = x1 + ux * pad, sy = y1 + uy * pad;
      const ex = x2 - ux * pad, ey = y2 - uy * pad;
      const ah = 9;
      const px = -uy * ah * 0.45, py = ux * ah * 0.45;
      const tip = `${ex},${ey}`;
      const b1  = `${ex - ux*ah + px},${ey - uy*ah + py}`;
      const b2  = `${ex - ux*ah - px},${ey - uy*ah - py}`;
      const color = TYPE_COLORS[src.type] || '#64748b';
      arrows.push({ key: `${src.stationId}→${dest.stationId}`, sx, sy, ex, ey, tip, b1, b2, color, label: dep.label });
    });
  });

  return (
    <svg style={{ position:'absolute', top:0, left:0, zIndex:0, pointerEvents:'none', overflow:'visible' }}
      width={gridW * CELL_TOTAL} height={gridH * CELL_TOTAL}>
      {arrows.map(a => (
        <g key={a.key}>
          <line x1={a.sx} y1={a.sy} x2={a.ex} y2={a.ey}
            stroke={a.color} strokeWidth={1.5} strokeOpacity={0.55} strokeDasharray="6 3" />
          <polygon points={`${a.tip} ${a.b1} ${a.b2}`} fill={a.color} fillOpacity={0.7} />
          {a.label && (
            <text x={(a.sx + a.ex) / 2} y={(a.sy + a.ey) / 2 - 5}
              fill={a.color} fontSize={10} textAnchor="middle" opacity={0.75}
              style={{ fontFamily:'system-ui', userSelect:'none' }}>
              {a.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

// ── Gate condition form ────────────────────────────────────────────────────────

function GateEditor({ gate, onChange, label }) {
  const empty = { gateType:'entry', logic:'AND', conditions:[], routes:[], label:'' };
  const g = gate || empty;

  const addCondition = () => onChange({ ...g,
    conditions: [...g.conditions, { field:'', operator:'==', value:'', label:'' }]
  });
  const updateCond = (i, upd) => onChange({ ...g,
    conditions: g.conditions.map((c,idx) => idx===i ? {...c,...upd} : c)
  });
  const removeCond = (i) => onChange({ ...g,
    conditions: g.conditions.filter((_,idx) => idx!==i)
  });

  const opStyle = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:4, padding:'3px 6px', fontSize:12 };

  return (
    <div style={{ padding:'10px 12px', background:'rgba(0,0,0,0.2)', borderRadius:6, marginBottom:8 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <span style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
          {label}
        </span>
        <select value={g.logic} onChange={e => onChange({...g, logic:e.target.value})} style={opStyle}>
          {['AND','OR','NOT'].map(l => <option key={l}>{l}</option>)}
        </select>
        <select value={g.gateType} onChange={e => onChange({...g, gateType:e.target.value})} style={opStyle}>
          {['entry','exit','dependency','decision','configuration','version','evidence','quality','exception','release']
            .map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      {g.conditions.map((cond, i) => (
        <div key={i} style={{ display:'flex', gap:4, marginBottom:4, alignItems:'center', flexWrap:'wrap' }}>
          <input placeholder="field" value={cond.field} onChange={e => updateCond(i,{field:e.target.value})}
            style={{ ...opStyle, flex:'2', minWidth:100, padding:'3px 6px' }} />
          <select value={cond.operator} onChange={e => updateCond(i,{operator:e.target.value})} style={opStyle}>
            {['==','!=','>=','<=','>','<','EXISTS','NOT_EXISTS','ONE_OF'].map(op => <option key={op}>{op}</option>)}
          </select>
          <input placeholder="value" value={String(cond.value||'')} onChange={e => updateCond(i,{value:e.target.value})}
            style={{ ...opStyle, flex:'2', minWidth:80, padding:'3px 6px' }} />
          <input placeholder="label (optional)" value={cond.label||''} onChange={e => updateCond(i,{label:e.target.value})}
            style={{ ...opStyle, flex:'3', minWidth:100, padding:'3px 6px' }} />
          <button onClick={() => removeCond(i)}
            style={{ background:'rgba(236,33,36,0.15)', border:'none', color:'#EC2124',
              borderRadius:4, padding:'3px 8px', cursor:'pointer', fontSize:11 }}>✕</button>
        </div>
      ))}
      <button onClick={addCondition}
        style={{ background:'rgba(70,187,137,0.12)', border:'1px solid rgba(70,187,137,0.3)',
          color:'var(--ni-green)', borderRadius:4, padding:'4px 10px', cursor:'pointer', fontSize:11, marginTop:2 }}>
        + Condition
      </button>
    </div>
  );
}

// ── Station side panel ────────────────────────────────────────────────────────

function StationPanel({ station, stations, canEdit, canConfigureExecution, onChange, onClose, onDelete, onDuplicate, getToken, bomItems }) {
  if (!station) return null;
  const s = station;
  const color = TYPE_COLORS[s.type] || '#64748b';

  const field = (label, children) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
        letterSpacing:'0.05em', marginBottom:5 }}>{label}</div>
      {children}
    </div>
  );

  const inputStyle = {
    background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'6px 10px', fontSize:13, width:'100%', boxSizing:'border-box',
  };

  const update = (patch) => onChange({ ...s, ...patch });

  // Control-plan line helpers (mirror the parts add/edit/remove pattern).
  const setControlItem = (i, patch) =>
    update({ controlItems: (s.controlItems || []).map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const removeControlItem = (i) =>
    update({ controlItems: (s.controlItems || []).filter((_, idx) => idx !== i) });
  const addControlItem = () =>
    update({ controlItems: [...(s.controlItems || []), {
      characteristic: '', charType: 'Product', classification: 'Standard', specification: '',
      measurementTechnique: '', sampleSize: '', sampleFrequency: '', controlMethod: '', reactionPlan: '',
    }] });

  return (
    <div style={{ width:320, borderLeft:'1px solid var(--ni-border-soft)',
      background:'var(--ni-char)', overflowY:'auto', display:'flex', flexDirection:'column', flexShrink:0 }}>
      {/* Header */}
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--ni-border-soft)',
        display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ width:10, height:10, borderRadius:3, background:color, flexShrink:0 }} />
        <span style={{ fontWeight:700, fontSize:14, flex:1 }}>{s.name || '(unnamed)'}</span>
        <span style={{ fontSize:11, color:'var(--ni-text-muted)', fontFamily:'monospace' }}>{s.stationId}</span>
        <button onClick={onClose}
          style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:16 }}>×</button>
      </div>

      <div style={{ padding:'16px', flex:1, overflowY:'auto' }}>
        {/* Name */}
        {field('Station Name',
          <input value={s.name||''} disabled={!canEdit}
            onChange={e => update({name:e.target.value})} style={inputStyle} />
        )}

        {/* Type */}
        {field('Station Type',
          canEdit ? (
            <select value={s.type||'Generic'} onChange={e => update({type:e.target.value})} style={inputStyle}>
              {STATION_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          ) : (
            <div style={{ ...inputStyle, color: color }}>{s.type}</div>
          )
        )}

        {/* Grid size */}
        {canEdit && field('Size (W × H cells)',
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input type="number" min={1} max={10} value={s.gridW||1}
              onChange={e => update({gridW:parseInt(e.target.value)||1})}
              style={{ ...inputStyle, width:64, textAlign:'center' }} />
            <span style={{color:'var(--ni-text-muted)'}}>×</span>
            <input type="number" min={1} max={10} value={s.gridH||1}
              onChange={e => update({gridH:parseInt(e.target.value)||1})}
              style={{ ...inputStyle, width:64, textAlign:'center' }} />
          </div>
        )}

        {/* Cycle time */}
        {field('Est. Cycle Time (sec)',
          <input type="number" min={0} value={s.estimatedCycleTimeSeconds||''}
            disabled={!canEdit}
            onChange={e => update({estimatedCycleTimeSeconds:parseInt(e.target.value)||null})}
            style={inputStyle} placeholder="e.g. 120" />
        )}

        {/* Operators */}
        {field('Operators Required',
          <input type="number" min={1} value={s.operatorCount||1}
            disabled={!canEdit}
            onChange={e => update({operatorCount:parseInt(e.target.value)||1})}
            style={inputStyle} />
        )}

        {/* Dependencies */}
        {field('Depends On (station IDs)',
          <div>
            {(s.dependencies||[]).map((dep, i) => (
              <div key={i} style={{ display:'flex', gap:4, marginBottom:4, alignItems:'center' }}>
                <select value={dep.fromStationId||''}
                  disabled={!canEdit}
                  onChange={e => {
                    const deps = [...(s.dependencies||[])];
                    deps[i] = {...deps[i], fromStationId:e.target.value};
                    update({dependencies:deps});
                  }}
                  style={{ ...inputStyle, flex:1, padding:'4px 8px', fontSize:12 }}
                >
                  <option value="">Select station…</option>
                  {stations.filter(st => st.stationId !== s.stationId).map(st => (
                    <option key={st.stationId} value={st.stationId}>{st.stationId} — {st.name||'unnamed'}</option>
                  ))}
                </select>
                <select value={dep.dependencyType||'full'}
                  disabled={!canEdit}
                  onChange={e => {
                    const deps = [...(s.dependencies||[])];
                    deps[i] = {...deps[i], dependencyType:e.target.value};
                    update({dependencies:deps});
                  }}
                  style={{ ...inputStyle, width:90, padding:'4px 6px', fontSize:11 }}
                >
                  <option value="full">Full</option>
                  <option value="partial_output">Partial</option>
                </select>
                {canEdit && (
                  <button onClick={() => {
                    const deps = (s.dependencies||[]).filter((_,idx)=>idx!==i);
                    update({dependencies:deps});
                  }} style={{ background:'rgba(236,33,36,0.15)', border:'none', color:'#EC2124',
                    borderRadius:4, padding:'4px 8px', cursor:'pointer', fontSize:11 }}>✕</button>
                )}
              </div>
            ))}
            {canEdit && (
              <button onClick={() => update({dependencies:[...(s.dependencies||[]),{fromStationId:'',dependencyType:'full'}]})}
                style={{ background:'rgba(70,187,137,0.12)', border:'1px solid rgba(70,187,137,0.3)',
                  color:'var(--ni-green)', borderRadius:4, padding:'4px 10px', cursor:'pointer', fontSize:11 }}>
                + Add Dependency
              </button>
            )}
          </div>
        )}

        {/* Gates */}
        {canEdit && (
          <>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
              letterSpacing:'0.05em', marginBottom:8 }}>Gate Logic</div>
            <GateEditor gate={s.entryGate} onChange={g => update({entryGate:g})} label="Entry Gate" />
            <GateEditor gate={s.exitGate}  onChange={g => update({exitGate:g})}  label="Exit Gate" />
          </>
        )}

        {/* Assembly Instructions */}
        {field('Assembly Instructions',
          <ResourcePicker
            endpoint="/quality/assembly-instructions" responseKey="items"
            items={s.assemblyInstructions||[]} getToken={getToken}
            onChange={v => update({ assemblyInstructions: v })}
            color="#2dd4bf" label="instructions"
            disabled={!canEdit} detailPath="/quality/assembly-instructions"
          />
        )}

        {/* Jigs */}
        {field('Jigs',
          <ResourcePicker
            endpoint="/quality/jigs" responseKey="jigs"
            items={s.jigs||[]} getToken={getToken}
            onChange={v => update({ jigs: v })}
            color="#a78bfa" label="jigs"
            disabled={!canEdit} detailPath="/quality/jigs"
          />
        )}

        {/* Equipment */}
        {field('Equipment',
          <ResourcePicker
            endpoint="/quality/equipment" responseKey="items"
            items={s.equipment||[]} getToken={getToken}
            onChange={v => update({ equipment: v })}
            color="#f59e0b" label="equipment"
            disabled={!canEdit} detailPath="/quality/equipment"
          />
        )}

        {/* Test Procedures */}
        {field('Test Procedures',
          <ResourcePicker
            endpoint="/quality/test-library" responseKey="entries"
            items={s.testProcedures||[]} getToken={getToken}
            onChange={v => update({ testProcedures: v })}
            color="#34d399" label="tests"
            disabled={!canEdit} detailPath="/quality/test-library"
          />
        )}

        {/* Parts (BOM) */}
        {field(`Parts (BOM)${(s.parts||[]).length ? ` — ${(s.parts||[]).length}` : ''}`,
          <div>
            {(s.parts||[]).map((p, i) => (
              canEdit ? (
                <div key={i} style={{ marginBottom:6, padding:6, background:'var(--ni-panel)',
                  border:'1px solid var(--ni-border-soft)', borderRadius:6 }}>
                  <div style={{ display:'flex', gap:4, marginBottom:4, alignItems:'center' }}>
                    <input value={p.partNumber||''} placeholder="Part number"
                      onChange={e => {
                        const parts = [...(s.parts||[])];
                        parts[i] = {...parts[i], partNumber:e.target.value};
                        update({parts});
                      }}
                      style={{ ...inputStyle, flex:1, padding:'4px 8px', fontSize:12, fontFamily:'monospace' }} />
                    <input type="number" min={0} step="any" value={p.quantity ?? 1}
                      onChange={e => {
                        const parts = [...(s.parts||[])];
                        parts[i] = {...parts[i], quantity:parseFloat(e.target.value)||0};
                        update({parts});
                      }}
                      style={{ ...inputStyle, width:52, padding:'4px 6px', fontSize:12, textAlign:'center' }} />
                    <input value={p.unit||'pcs'} placeholder="unit"
                      onChange={e => {
                        const parts = [...(s.parts||[])];
                        parts[i] = {...parts[i], unit:e.target.value};
                        update({parts});
                      }}
                      style={{ ...inputStyle, width:48, padding:'4px 6px', fontSize:12 }} />
                    <button onClick={() => update({parts:(s.parts||[]).filter((_,idx)=>idx!==i)})}
                      style={{ background:'rgba(236,33,36,0.15)', border:'none', color:'#EC2124',
                        borderRadius:4, padding:'4px 8px', cursor:'pointer', fontSize:11 }}>✕</button>
                  </div>
                  <input value={p.name||''} placeholder="Description"
                    onChange={e => {
                      const parts = [...(s.parts||[])];
                      parts[i] = {...parts[i], name:e.target.value};
                      update({parts});
                    }}
                    style={{ ...inputStyle, padding:'4px 8px', fontSize:12 }} />
                </div>
              ) : (
                <div key={i} style={{ marginBottom:4, padding:'5px 8px', background:'var(--ni-panel)',
                  border:'1px solid var(--ni-border-soft)', borderRadius:6 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'baseline' }}>
                    <span style={{ fontSize:12, color:'#60a5fa', fontFamily:'monospace' }}>{p.partNumber||'—'}</span>
                    <span style={{ fontSize:11, color:'var(--ni-text-muted)', whiteSpace:'nowrap' }}>
                      {p.quantity ?? 1} {p.unit||'pcs'}
                    </span>
                  </div>
                  {p.name && (
                    <div style={{ fontSize:11, color:'var(--ni-text-muted)', marginTop:2 }}>{p.name}</div>
                  )}
                </div>
              )
            ))}
            {!(s.parts||[]).length && !canEdit && (
              <div style={{ fontSize:12, color:'var(--ni-text-muted)' }}>No parts linked.</div>
            )}
            {canEdit && (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {(bomItems||[]).length > 0 && (
                  <select value="" onChange={e => {
                      const item = (bomItems||[]).find(b => b.ninPartNumber === e.target.value);
                      if (!item) return;
                      update({ parts:[...(s.parts||[]), {
                        partNumber: item.ninPartNumber, name: item.description || '',
                        quantity: item.quantity ?? 1, unit: item.unit || 'pcs',
                      }] });
                    }}
                    style={{ ...inputStyle, padding:'5px 8px', fontSize:12, borderColor:BOM_ACCENT+'66' }}>
                    <option value="">+ Add from BOM…</option>
                    {(bomItems||[]).map(item => (
                      <option key={item.ninPartNumber} value={item.ninPartNumber}>
                        {item.ninPartNumber}{item.description ? ` — ${item.description}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <button onClick={() => update({parts:[...(s.parts||[]),{partNumber:'',name:'',quantity:1,unit:'pcs'}]})}
                  style={{ background:'rgba(70,187,137,0.12)', border:'1px solid rgba(70,187,137,0.3)',
                    color:'var(--ni-green)', borderRadius:4, padding:'4px 10px', cursor:'pointer', fontSize:11, alignSelf:'flex-start' }}>
                  + Add Part
                </button>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {field('Station Notes',
          <textarea value={s.notes||''} disabled={!canEdit} rows={3}
            onChange={e => update({notes:e.target.value})}
            style={{ ...inputStyle, resize:'vertical' }} placeholder="Notes, instructions…" />
        )}

        <StationExecutionSettings value={s.execution} disabled={!canConfigureExecution}
          onChange={execution => update({ execution })} />

        {/* Control plan (IATF 16949 / APQP) — characteristics controlled here */}
        {field(`Control Plan${(s.controlItems||[]).length ? ` — ${(s.controlItems||[]).length}` : ''}`,
          <div>
            {(s.controlItems||[]).map((ci, i) => (
              canEdit ? (
                <div key={i} style={{ marginBottom:8, padding:8, background:'var(--ni-panel)',
                  border:'1px solid var(--ni-border-soft)', borderRadius:6 }}>
                  <div style={{ display:'flex', gap:4, marginBottom:4, alignItems:'center' }}>
                    <input value={ci.characteristic||''} placeholder="Characteristic"
                      onChange={e => setControlItem(i, { characteristic:e.target.value })}
                      style={{ ...inputStyle, flex:1, padding:'4px 8px', fontSize:12 }} />
                    <button onClick={() => removeControlItem(i)}
                      style={{ background:'rgba(236,33,36,0.15)', border:'none', color:'#EC2124',
                        borderRadius:4, padding:'4px 8px', cursor:'pointer', fontSize:11 }}>✕</button>
                  </div>
                  <div style={{ display:'flex', gap:4, marginBottom:4 }}>
                    <select value={ci.charType||'Product'} onChange={e => setControlItem(i, { charType:e.target.value })}
                      style={{ ...inputStyle, flex:1, padding:'4px 6px', fontSize:11 }}>
                      {CONTROL_CHAR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={ci.classification||'Standard'} onChange={e => setControlItem(i, { classification:e.target.value })}
                      style={{ ...inputStyle, flex:1, padding:'4px 6px', fontSize:11 }}>
                      {CONTROL_CLASSIFICATIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <input value={ci.specification||''} placeholder="Specification / tolerance"
                    onChange={e => setControlItem(i, { specification:e.target.value })}
                    style={{ ...inputStyle, padding:'4px 8px', fontSize:12, marginBottom:4 }} />
                  <input value={ci.measurementTechnique||''} placeholder="Measurement technique / gauge"
                    onChange={e => setControlItem(i, { measurementTechnique:e.target.value })}
                    style={{ ...inputStyle, padding:'4px 8px', fontSize:12, marginBottom:4 }} />
                  <div style={{ display:'flex', gap:4, marginBottom:4 }}>
                    <input value={ci.sampleSize||''} placeholder="Sample size"
                      onChange={e => setControlItem(i, { sampleSize:e.target.value })}
                      style={{ ...inputStyle, flex:1, padding:'4px 6px', fontSize:11 }} />
                    <input value={ci.sampleFrequency||''} placeholder="Frequency"
                      onChange={e => setControlItem(i, { sampleFrequency:e.target.value })}
                      style={{ ...inputStyle, flex:1, padding:'4px 6px', fontSize:11 }} />
                  </div>
                  <input value={ci.controlMethod||''} placeholder="Control method (SPC, checklist, 100%…)"
                    onChange={e => setControlItem(i, { controlMethod:e.target.value })}
                    style={{ ...inputStyle, padding:'4px 8px', fontSize:12, marginBottom:4 }} />
                  <input value={ci.reactionPlan||''} placeholder="Reaction plan"
                    onChange={e => setControlItem(i, { reactionPlan:e.target.value })}
                    style={{ ...inputStyle, padding:'4px 8px', fontSize:12 }} />
                </div>
              ) : (
                <div key={i} style={{ marginBottom:4, padding:'6px 8px', background:'var(--ni-panel)',
                  border:'1px solid var(--ni-border-soft)', borderRadius:6 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'baseline' }}>
                    <span style={{ fontSize:12, fontWeight:600 }}>{ci.characteristic||'—'}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:CLASS_COLORS[ci.classification]||'#94a3b8' }}>
                      {ci.classification||'Standard'}
                    </span>
                  </div>
                  {ci.specification && <div style={{ fontSize:11, color:'var(--ni-text-muted)', marginTop:2 }}>{ci.specification}</div>}
                  {(ci.measurementTechnique||ci.controlMethod) && (
                    <div style={{ fontSize:10, color:'var(--ni-text-muted)', marginTop:2 }}>
                      {[ci.measurementTechnique, ci.controlMethod].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {(ci.sampleSize||ci.sampleFrequency) && (
                    <div style={{ fontSize:10, color:'var(--ni-text-muted)', marginTop:2 }}>
                      Sample: {[ci.sampleSize, ci.sampleFrequency].filter(Boolean).join(' / ')}
                    </div>
                  )}
                  {ci.reactionPlan && <div style={{ fontSize:10, color:'var(--ni-text-muted)', marginTop:2 }}>React: {ci.reactionPlan}</div>}
                </div>
              )
            ))}
            {!(s.controlItems||[]).length && !canEdit && (
              <div style={{ fontSize:12, color:'var(--ni-text-muted)' }}>No control items.</div>
            )}
            {canEdit && (
              <button onClick={addControlItem}
                style={{ background:'rgba(70,187,137,0.12)', border:'1px solid rgba(70,187,137,0.3)',
                  color:'var(--ni-green)', borderRadius:4, padding:'4px 10px', cursor:'pointer', fontSize:11 }}>
                + Add Control Item
              </button>
            )}
          </div>
        )}

        {/* Duplicate / Delete */}
        {canEdit && (
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={onDuplicate}
              style={{ flex:1, background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.3)',
                color:'#60a5fa', borderRadius:6, padding:'8px 10px', cursor:'pointer', fontSize:12, fontWeight:600 }}>
              Duplicate
            </button>
            <button onClick={onDelete}
              style={{ flex:1, background:'rgba(236,33,36,0.12)', border:'1px solid rgba(236,33,36,0.3)',
                color:'#EC2124', borderRadius:6, padding:'8px 10px', cursor:'pointer', fontSize:12, fontWeight:600 }}>
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main canvas component ─────────────────────────────────────────────────────

export default function ProcessFlowCanvas() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRevision = searchParams.get('revision') || '';
  const [availableRevisions, setAvailableRevisions] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [showDocumentControl, setShowDocumentControl] = useState(false);
  const [jsonBusy, setJsonBusy] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const loadSequence = useRef(0);
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();

  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = ['admin','quality-manager','production-engineer','service'].includes(role);
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));

  const [flow, setFlow] = useState(null);
  const canEdit = !requestedRevision && !snapshot && flow?.currentStatus === 'Draft'
    && (canManage || (supplierKey && (flow?.createdBy === user?.id
      || ((flow?.visibility?.suppliers || []).includes(String(role).replace(/^test-/,''))
        && (flow?.revisions || []).some(r => r.status === 'Released')))));
  const [stations, setStations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPalette, setShowPalette] = useState(true);
  const [showArrows, setShowArrows]   = useState(true);
  const [showBom, setShowBom]         = useState(false);
  const [bomItems, setBomItems] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [changeSummary, setChangeSummary] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusAction, setStatusAction] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [exporting, setExporting] = useState('');
  const [coverageWarning, setCoverageWarning] = useState(null);
  const [showControlPlan, setShowControlPlan] = useState(false);
  const [cpForm, setCpForm] = useState(null);
  const [cpSaving, setCpSaving] = useState(false);

  const gridRef = useRef(null);

  // ── Export Manufacturing Release Package (PDF, or ZIP incl. datasheets for
  // every referenced document) ────────────────────────────────────────────────
  const exportPackage = useCallback(async (format) => {
    setExporting(format);
    setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/process-flows/${id}/export.${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(flow?.documentNumber || 'flow')}_Rev${flow?.currentRevision || 'A1'}_ReleasePackage.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
    finally { setExporting(''); }
  }, [getToken, id, flow]);

  // ── Load flow ───────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const sequence = ++loadSequence.current;
      setLoading(true); setError('');
      try {
        const token = await getToken();
        const query = requestedRevision ? `?revision=${encodeURIComponent(requestedRevision)}` : '';
        const res = await window.fetch(`${API_BASE}/quality/process-flows/${id}${query}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (sequence !== loadSequence.current) return;
        if (res.ok && data.ok) {
          setFlow(data.flow);
          setStations(data.flow.stations || []);
          setAvailableRevisions(data.availableRevisions || []); setSnapshot(data.snapshot || null);
          setDirty(false); setSelectedId(null);
        } else {
          setFlow(null); setError(data.message || data.error || 'Failed to load');
        }
      } catch (err) { if (sequence === loadSequence.current) { setFlow(null); setError(err.message); } }
      finally { if (sequence === loadSequence.current) setLoading(false); }
    })();
  }, [id, getToken, requestedRevision, reloadVersion]);

  // ── Load attached BOM items ───────────────────────────────────────────────────
  useEffect(() => {
    const bomId = flow?.bomId;
    if (!bomId) { setBomItems([]); return; }
    (async () => {
      try {
        const token = await getToken();
        const res = await window.fetch(`${API_BASE}/quality/boms/${bomId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.ok) setBomItems(data.bom.items || []);
      } catch { /* ignore */ }
    })();
  }, [flow?.bomId, getToken]);

  // ── Save canvas ─────────────────────────────────────────────────────────────
  const saveCanvas = useCallback(async (summary) => {
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/process-flows/${id}/stations`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stations, ...(canManage ? { executionPolicy:flow.executionPolicy || { enabled:false }, sites:flow.sites || [] } : {}),
          changeSummary: summary || 'Canvas updated', expectedVersion:flow.__v }),
      });
      const data = await res.json();
      if (data.ok) {
        setFlow(data.flow);
        setDirty(false);
        setChangeSummary('');
        setShowSaveModal(false);
      } else {
        setError(data.error);
      }
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }, [id, getToken, stations, canEdit, canManage, flow]);

  // ── Status action ────────────────────────────────────────────────────────────
  const doStatusAction = useCallback(async () => {
    if (requestedRevision || snapshot || dirty) return;
    setSaving(true);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/process-flows/${id}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: statusAction, changeSummary: statusNote, expectedVersion:flow.__v }),
      });
      const data = await res.json();
      if (data.ok) { setFlow(data.flow); setShowStatusModal(false); setStatusNote(''); }
      else setError(data.error);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }, [id, getToken, statusAction, statusNote, flow, requestedRevision, snapshot, dirty]);

  // ── Control-plan header (flow-level metadata, saved via PATCH like name/desc) ──
  const openControlPlan = () => {
    setCpForm({
      controlPlanType: flow?.controlPlanType || '',
      controlPlanNumber: flow?.controlPlanNumber || '',
      controlPlanContact: flow?.controlPlanContact || '',
      controlPlanDate: flow?.controlPlanDate ? String(flow.controlPlanDate).slice(0, 10) : '',
      controlPlanTeam: flow?.controlPlanTeam || '',
    });
    setShowControlPlan(true);
  };
  const saveControlPlan = useCallback(async () => {
    if (!cpForm || !canManage || !canEdit) return;
    setCpSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/process-flows/${id}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cpForm, controlPlanDate: cpForm.controlPlanDate || null, expectedVersion:flow.__v }),
      });
      const data = await res.json();
      if (data.ok) { setFlow(data.flow); setShowControlPlan(false); }
      else setError(data.error);
    } catch (e) { setError(e.message); } finally { setCpSaving(false); }
  }, [id, getToken, cpForm, canManage, canEdit, flow]);

  // ── BOM coverage gate ─────────────────────────────────────────────────────────
  // Before saving or submitting, if a BOM is attached and some of its items are
  // not used by any station, warn (but do not block). `proceed` is the action to
  // run once the user dismisses the warning or when coverage is complete.
  const withCoverageCheck = useCallback((proceed) => {
    const unused = flow?.bomId ? computeUnusedBomItems(bomItems, stations) : [];
    if (unused.length) {
      setCoverageWarning({ unused, proceed });
    } else {
      proceed();
    }
  }, [flow?.bomId, bomItems, stations]);

  // ── Station mutations ────────────────────────────────────────────────────────
  const addStation = (type) => {
    const newStation = {
      stationId: genId(stations), name: type, type, color: '',
      gridX: 0, gridY: 0, gridW: 1, gridH: 1,
      logicalX: 0, logicalY: 0,
      procedures: [], assemblyInstructions: [], jigs: [], equipment: [], testProcedures: [], parts: [],
      outputs: [], dependencies: [],
      entryGate: null, exitGate: null, additionalGates: [],
      notes: '', estimatedCycleTimeSeconds: null, operatorCount: 1,
      controlItems: [],
    };
    setStations(prev => [...prev, newStation]);
    setSelectedId(newStation.stationId);
    setDirty(true);
  };

  const updateStation = (updated) => {
    setStations(prev => prev.map(s => s.stationId === updated.stationId ? updated : s));
    setDirty(true);
  };

  const duplicateStation = (station) => {
    const gw = flow.gridWidth  || 10;
    const gh = flow.gridHeight || 10;
    const newStation = {
      ...station,
      stationId: genId(stations),
      name: station.name + ' (Copy)',
      gridX: Math.min(station.gridX + 2, gw - (station.gridW || 1)),
      gridY: Math.min(station.gridY + 2, gh - (station.gridH || 1)),
      dependencies: [],
    };
    setStations(prev => [...prev, newStation]);
    setSelectedId(newStation.stationId);
    setDirty(true);
  };

  const deleteStation = (stationId) => {
    setStations(prev => {
      const next = prev.filter(s => s.stationId !== stationId);
      // Remove this station from all dependency lists
      return next.map(s => ({
        ...s,
        dependencies: (s.dependencies||[]).filter(d => d.fromStationId !== stationId),
      }));
    });
    setSelectedId(null);
    setDirty(true);
  };

  // ── Grid drag/drop ────────────────────────────────────────────────────────────
  const handleDragStart = (e, stationId) => {
    setDraggingId(stationId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCellDrop = (e, cellX, cellY) => {
    e.preventDefault();
    if (!draggingId) return;
    setStations(prev => prev.map(s =>
      s.stationId === draggingId ? { ...s, gridX: cellX, gridY: cellY } : s
    ));
    setDraggingId(null);
    setDirty(true);
  };

  const handleCellDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: 40, color: 'var(--ni-text-muted)', textAlign: 'center' }}>Loading…</div>;
  if (!flow)   return <div style={{ padding: 40, color: '#EC2124' }}>Error: {error}</div>;

  const gridW = flow.gridWidth  || 10;
  const gridH = flow.gridHeight || 10;
  const selectedStation = stations.find(s => s.stationId === selectedId) || null;

  const availableActions = [];
  if (flow.currentStatus === 'Draft')     availableActions.push({ action:'submit',   label:'Submit for Review' });
  if (flow.currentStatus === 'In Review') availableActions.push({ action:'approve',  label:'Approve & Release' });
  if (flow.currentStatus === 'In Review') availableActions.push({ action:'reject',   label:'Reject to Draft' });
  if (flow.currentStatus === 'Released')  availableActions.push({ action:'revise',    label:'Revise / New Draft' });
  if (flow.currentStatus === 'Released')  availableActions.push({ action:'supersede', label:'Supersede' });
  // Which status actions this user may take: staff do everything; a supplier may
  // submit a draft they're editing or revise a released flow shared with them.
  const mayAct = (action) => !requestedRevision && !snapshot && !dirty && (canManage || (supplierKey && (
    (action === 'submit' && flow.currentStatus === 'Draft') ||
    (action === 'revise' && flow.currentStatus === 'Released')
  ))) && !(action === 'approve' && (flow.releaseBlockers || []).some(b => !b.resolved));

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 52px)', overflow:'hidden',
      margin:'-32px -24px', padding:0 }}>

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={{ padding:'8px 16px', borderBottom:'1px solid var(--ni-border-soft)',
        background:'var(--ni-char)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <button onClick={() => navigate('/quality/process')}
          style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:13, padding:0 }}>
          ← Flows
        </button>
        <span style={{ color:'var(--ni-text-muted)' }}>|</span>
        <span style={{ fontWeight:700, fontSize:14 }}>{flow.name}</span>
        <span style={{ fontFamily:'monospace', fontSize:12, color:'var(--ni-text-muted)' }}>{flow.documentNumber}</span>
        <StatusBadge status={flow.currentStatus} />
        <span style={{ fontSize:12, color:'var(--ni-text-muted)', fontFamily:'monospace' }}>Rev. {flow.currentRevision}</span>

        <div style={{ flex:1 }} />

        {/* Status action buttons */}
        {availableActions.filter(a => mayAct(a.action)).map(({ action, label }) => (
          <button key={action}
            onClick={() => {
              const open = () => { setStatusAction(action); setShowStatusModal(true); };
              if (action === 'submit') withCoverageCheck(open); else open();
            }}
            style={{ background:'rgba(255,255,255,0.07)', border:'1px solid var(--ni-border-soft)',
              color:'var(--ni-text)', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
            {label}
          </button>
        ))}

        {/* Export Manufacturing Release Package */}
        <button onClick={() => exportPackage('pdf')} disabled={!!exporting || !!requestedRevision || !!snapshot}
          title={flow.currentStatus === 'Released'
            ? 'Export the Manufacturing Release Package (PDF)'
            : 'Export PDF — flow is not Released, the document will carry a DRAFT watermark'}
          style={{ background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.35)',
            color: 'var(--ni-green)', borderRadius: 6, padding: '5px 12px', fontSize: 12,
            cursor: exporting ? 'wait' : 'pointer', fontWeight: 700, opacity: exporting === 'pdf' ? 0.6 : 1 }}>
          {exporting === 'pdf' ? 'Exporting…' : '📄 PDF'}
        </button>
        <button onClick={() => exportPackage('zip')} disabled={!!exporting || !!requestedRevision || !!snapshot}
          title="Export the release package PDF plus a 'Referenced documents' folder with a generated datasheet for every linked procedure, jig, equipment item and test"
          style={{ background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.35)',
            color: 'var(--ni-green)', borderRadius: 6, padding: '5px 12px', fontSize: 12,
            cursor: exporting ? 'wait' : 'pointer', fontWeight: 700, opacity: exporting === 'zip' ? 0.6 : 1 }}>
          {exporting === 'zip' ? 'Exporting…' : '📦 ZIP + references'}
        </button>

        <button onClick={() => setShowDocumentControl(true)}
          style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' }}>Document control / JSON</button>

        {/* Control-plan header */}
        <button onClick={openControlPlan}
          title="Control-plan header (type, number, contact, date, team). Per-station control items are edited in each station panel."
          style={{ background: (flow.controlPlanType || flow.controlPlanNumber) ? 'rgba(70,187,137,0.12)' : 'transparent',
            border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)',
            borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
          🎛️ Control Plan
        </button>

        {/* Arrows toggle */}
        <button onClick={() => setShowArrows(p => !p)}
          style={{ background: showArrows ? 'rgba(96,165,250,0.15)' : 'transparent',
            border:'1px solid var(--ni-border-soft)', color: showArrows ? '#60a5fa' : 'var(--ni-text-muted)',
            borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
          {showArrows ? '⟶ Arrows On' : '⟶ Arrows Off'}
        </button>

        {/* Show palette toggle */}
        {canEdit && (
          <button onClick={() => setShowPalette(p => !p)}
            style={{ background: showPalette ? 'rgba(96,165,250,0.15)' : 'transparent',
              border:'1px solid var(--ni-border-soft)', color: showPalette ? '#60a5fa' : 'var(--ni-text-muted)',
              borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
            {showPalette ? 'Hide Palette' : 'Show Palette'}
          </button>
        )}

        {/* BOM split-view toggle */}
        {flow.bomId && (
          <button onClick={() => setShowBom(p => !p)}
            title={flow.bomName ? `Attached BOM: ${flow.bomName}` : 'Attached BOM'}
            style={{ background: showBom ? BOM_ACCENT+'26' : 'transparent',
              border:`1px solid ${showBom ? BOM_ACCENT : 'var(--ni-border-soft)'}`,
              color: showBom ? BOM_ACCENT : 'var(--ni-text-muted)',
              borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
            {showBom ? '🧾 BOM On' : '🧾 BOM'}
          </button>
        )}

        {/* Save */}
        {canEdit && dirty && (
          <button onClick={() => withCoverageCheck(() => setShowSaveModal(true))} disabled={saving}
            style={{ background:'#60a5fa', color:'#000', border:'none', borderRadius:6,
              padding:'5px 14px', fontSize:12, cursor:'pointer', fontWeight:700 }}>
            {saving ? 'Saving…' : '💾 Save Canvas'}
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding:'8px 16px', background:'rgba(236,33,36,0.12)', color:'#EC2124', fontSize:13 }}>
          {error}
        </div>
      )}

      {/* ── Body (palette + grid + panel) ───────────────────────────────────── */}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

        {/* Palette */}
        {showPalette && canEdit && (
          <div style={{ width:180, borderRight:'1px solid var(--ni-border-soft)',
            background:'var(--ni-char)', overflowY:'auto', padding:12, flexShrink:0 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
              letterSpacing:'0.05em', marginBottom:8 }}>Add Station</div>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {STATION_TYPES.map(type => (
                <PaletteItem key={type} type={type} onAdd={addStation} />
              ))}
            </div>
          </div>
        )}

        {/* Grid */}
        <div style={{ flex:1, overflow:'auto', padding:16, background:'rgba(0,0,0,0.15)' }}>
          <div
            ref={gridRef}
            style={{
              display:'grid',
              gridTemplateColumns: `repeat(${gridW}, ${CELL_PX}px)`,
              gridTemplateRows:    `repeat(${gridH}, ${CELL_PX}px)`,
              gap:2,
              position:'relative',
              width: gridW * (CELL_PX + 2),
              height: gridH * (CELL_PX + 2),
            }}
          >
            {/* Grid cells (drop targets) */}
            {Array.from({ length: gridH }).map((_, row) =>
              Array.from({ length: gridW }).map((_, col) => (
                <div
                  key={`${col}-${row}`}
                  onDrop={canEdit ? (e) => handleCellDrop(e, col, row) : undefined}
                  onDragOver={canEdit ? handleCellDragOver : undefined}
                  style={{
                    gridColumn: col + 1,
                    gridRow:    row + 1,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.04)',
                    borderRadius: 3,
                    position: 'relative',
                    minHeight: CELL_PX,
                    minWidth:  CELL_PX,
                  }}
                >
                  {/* Coordinate label in corner */}
                  <span style={{ position:'absolute', bottom:2, right:4,
                    fontSize:9, color:'rgba(255,255,255,0.1)', fontFamily:'monospace', userSelect:'none' }}>
                    {col},{row}
                  </span>
                </div>
              ))
            )}

            {/* Dependency arrows */}
            {showArrows && (
              <ArrowOverlay stations={stations} gridW={gridW} gridH={gridH} />
            )}

            {/* Station blocks (rendered on top of cells) */}
            {stations.map(station => (
              <StationBlock
                key={station.stationId}
                station={station}
                selected={station.stationId === selectedId}
                canEdit={canEdit}
                onSelect={setSelectedId}
                onDragStart={handleDragStart}
              />
            ))}
          </div>

          {/* Grid resize controls */}
          {canEdit && (
            <div style={{ marginTop:12, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontSize:12, color:'var(--ni-text-muted)' }}>Grid:</span>
              <button onClick={() => { setFlow(f => ({...f, gridWidth:  Math.max(1, (f.gridWidth||10)-1)})); setDirty(true); }}
                style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)',
                  borderRadius:4, padding:'3px 10px', cursor:'pointer', fontSize:12 }}>−W</button>
              <span style={{ fontSize:12, color:'var(--ni-text-muted)' }}>{gridW}×{gridH}</span>
              <button onClick={() => { setFlow(f => ({...f, gridWidth:  Math.min(30, (f.gridWidth||10)+1)})); setDirty(true); }}
                style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)',
                  borderRadius:4, padding:'3px 10px', cursor:'pointer', fontSize:12 }}>+W</button>
              <button onClick={() => { setFlow(f => ({...f, gridHeight: Math.max(1, (f.gridHeight||10)-1)})); setDirty(true); }}
                style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)',
                  borderRadius:4, padding:'3px 10px', cursor:'pointer', fontSize:12 }}>−H</button>
              <button onClick={() => { setFlow(f => ({...f, gridHeight: Math.min(30, (f.gridHeight||10)+1)})); setDirty(true); }}
                style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)',
                  borderRadius:4, padding:'3px 10px', cursor:'pointer', fontSize:12 }}>+H</button>
            </div>
          )}
        </div>

        {/* BOM split-view panel */}
        {showBom && flow.bomId && (
          <BomPanel
            bomItems={bomItems}
            stations={stations}
            flow={flow}
            onClose={() => setShowBom(false)}
          />
        )}

        {/* Station panel */}
        {selectedStation && (
          <StationPanel
            station={selectedStation}
            stations={stations}
            canEdit={canEdit}
            canConfigureExecution={canEdit && canManage}
            onChange={updateStation}
            onClose={() => setSelectedId(null)}
            onDelete={() => deleteStation(selectedStation.stationId)}
            onDuplicate={() => duplicateStation(selectedStation)}
            getToken={getToken}
            bomItems={bomItems}
          />
        )}
      </div>

      {/* ── Save modal ──────────────────────────────────────────────────────── */}
      {showSaveModal && (
        <Modal title="Save Canvas" onClose={() => setShowSaveModal(false)}>
          <p style={{ color:'var(--ni-text-muted)', fontSize:13, margin:'0 0 12px' }}>
            Describe what changed (optional):
          </p>
          <textarea value={changeSummary} onChange={e => setChangeSummary(e.target.value)}
            rows={3} placeholder="e.g. Added burn-in station, updated entry gates"
            style={{ width:'100%', boxSizing:'border-box', background:'var(--ni-panel)',
              border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)', borderRadius:6,
              padding:'8px 10px', fontSize:13, resize:'vertical', marginBottom:12 }} />
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => saveCanvas(changeSummary)}
              style={{ background:'#60a5fa', color:'#000', border:'none', borderRadius:6,
                padding:'8px 20px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              Save
            </button>
            <button onClick={() => setShowSaveModal(false)}
              style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                borderRadius:6, padding:'8px 16px', fontSize:13, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* ── Status action modal ─────────────────────────────────────────────── */}
      {showStatusModal && (
        <Modal title={availableActions.find(a => a.action === statusAction)?.label || statusAction}
          onClose={() => setShowStatusModal(false)}>
          <textarea value={statusNote} onChange={e => setStatusNote(e.target.value)}
            rows={3} placeholder="Change note (optional)"
            style={{ width:'100%', boxSizing:'border-box', background:'var(--ni-panel)',
              border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)', borderRadius:6,
              padding:'8px 10px', fontSize:13, resize:'vertical', marginBottom:12 }} />
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={doStatusAction}
              style={{ background:'var(--ni-green)', color:'#000', border:'none', borderRadius:6,
                padding:'8px 20px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              Confirm
            </button>
            <button onClick={() => setShowStatusModal(false)}
              style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                borderRadius:6, padding:'8px 16px', fontSize:13, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* ── BOM coverage warning modal ──────────────────────────────────────── */}
      {coverageWarning && (
        <Modal title="Unused BOM items" onClose={() => setCoverageWarning(null)}>
          <p style={{ color:'var(--ni-text)', fontSize:13, margin:'0 0 12px' }}>
            You have <strong style={{ color:BOM_ACCENT }}>{coverageWarning.unused.length}</strong>{' '}
            BOM {coverageWarning.unused.length === 1 ? 'item' : 'items'} not used by any station.
            You can still proceed.
          </p>
          <details style={{ marginBottom:14 }}>
            <summary style={{ cursor:'pointer', fontSize:12, fontWeight:700, color:'var(--ni-text-muted)',
              textTransform:'uppercase', letterSpacing:'0.05em' }}>
              Show unused items
            </summary>
            <div style={{ marginTop:8, maxHeight:220, overflowY:'auto', border:'1px solid var(--ni-border-soft)',
              borderRadius:6 }}>
              {coverageWarning.unused.map((item, i) => (
                <div key={item.ninPartNumber || i}
                  style={{ display:'flex', gap:8, padding:'6px 10px', alignItems:'baseline',
                    borderTop: i ? '1px solid var(--ni-border-soft)' : 'none' }}>
                  <span style={{ fontSize:12, color:BOM_ACCENT, fontFamily:'monospace', flexShrink:0 }}>
                    {item.ninPartNumber || '—'}
                  </span>
                  <span style={{ fontSize:12, color:'var(--ni-text-muted)' }}>{item.description || ''}</span>
                </div>
              ))}
            </div>
          </details>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => { const fn = coverageWarning.proceed; setCoverageWarning(null); fn(); }}
              style={{ background:'#60a5fa', color:'#000', border:'none', borderRadius:6,
                padding:'8px 20px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              Proceed Anyway
            </button>
            <button onClick={() => setCoverageWarning(null)}
              style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                borderRadius:6, padding:'8px 16px', fontSize:13, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {showDocumentControl && <Modal title="Process flow document control" onClose={() => setShowDocumentControl(false)}>
        <ProcedureRevisionBar entry={flow} requestedRevision={requestedRevision} revisions={availableRevisions} snapshot={snapshot}
          allowAuthorDownload={false} revisionLocked={dirty || saving || jsonBusy || showControlPlan} onRevisionChange={revision => { if (!(dirty || saving || jsonBusy || showControlPlan)) setSearchParams(revision ? { revision } : {}); }} />
        {dirty && <p>Save or cancel canvas edits before changing the viewed revision or applying JSON.</p>}
        <ControlledDraftJson library="process-flows" record={flow} revision={requestedRevision || snapshot?.revision || ''}
          blocked={dirty || saving || showControlPlan} onBusyChange={setJsonBusy} onApplied={() => setReloadVersion(v => v+1)} />
        <ProcedureAuthoringMetadata value={flow} staff={canManage} />
        <FlowExecutionPolicy value={flow.executionPolicy} disabled={!canEdit || !canManage || saving || jsonBusy}
          sites={flow.sites || []} onSitesChange={sites => { setFlow(f => ({ ...f, sites })); setDirty(true); }}
          onChange={executionPolicy => { setFlow(f => ({ ...f, executionPolicy })); setDirty(true); }} />
        <h3 style={{ fontSize:14 }}>Pinned BOM review context</h3>
        <p style={{ fontSize:12, color:'var(--ni-text-muted)' }}>These references describe applicability for review; they do not change station execution.</p>
        {(flow.bomReferences || []).map((r,i) => <div key={i}>
          <PinnedReferences value={[{ ...r, type:'bom' }]} />
          <p style={{ fontSize:12 }}>{(r.applicableProducts || []).join(', ') || 'Applicability not specified'}</p>
        </div>)}
      </Modal>}

      {/* ── Control-plan header modal ────────────────────────────────────────── */}
      {showControlPlan && cpForm && (() => {
        const mInp = { width:'100%', boxSizing:'border-box', background:'var(--ni-panel)',
          border:'1px solid var(--ni-border-soft)', color:'var(--ni-text)', borderRadius:6, padding:'8px 10px', fontSize:13 };
        const mLbl = { fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
          letterSpacing:'0.05em', marginBottom:5, display:'block' };
        const set = (k, v) => setCpForm(f => ({ ...f, [k]: v }));
        return (
          <Modal title="Control Plan Header" onClose={() => setShowControlPlan(false)}>
            <p style={{ color:'var(--ni-text-muted)', fontSize:12, margin:'0 0 14px' }}>
              IATF 16949 / APQP control-plan header — assigned by North Invent quality staff.
              Per-station control characteristics are edited in each station's panel and saved with the canvas.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1 }}>
                  <label style={mLbl}>Plan type</label>
                  <select value={cpForm.controlPlanType} disabled={!canManage || !canEdit} onChange={e => set('controlPlanType', e.target.value)} style={mInp}>
                    {CONTROL_PLAN_TYPES.map(t => <option key={t || 'any'} value={t}>{t || '—'}</option>)}
                  </select>
                </div>
                <div style={{ flex:1 }}>
                  <label style={mLbl}>Plan number</label>
                  <input value={cpForm.controlPlanNumber} disabled={!canManage || !canEdit} onChange={e => set('controlPlanNumber', e.target.value)} style={mInp} placeholder="e.g. CP-WE270-01" />
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1 }}>
                  <label style={mLbl}>Key contact</label>
                  <input value={cpForm.controlPlanContact} disabled={!canManage || !canEdit} onChange={e => set('controlPlanContact', e.target.value)} style={mInp} />
                </div>
                <div style={{ flex:1 }}>
                  <label style={mLbl}>Date</label>
                  <input type="date" value={cpForm.controlPlanDate} disabled={!canManage || !canEdit} onChange={e => set('controlPlanDate', e.target.value)} style={mInp} />
                </div>
              </div>
              <div>
                <label style={mLbl}>Team</label>
                <input value={cpForm.controlPlanTeam} disabled={!canManage || !canEdit} onChange={e => set('controlPlanTeam', e.target.value)} style={mInp} placeholder="Cross-functional team members" />
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {canManage && canEdit && (
                  <button onClick={saveControlPlan} disabled={cpSaving}
                    style={{ background:'var(--ni-green)', color:'#000', border:'none', borderRadius:6,
                      padding:'8px 20px', fontWeight:700, fontSize:13, cursor:cpSaving ? 'wait' : 'pointer' }}>
                    {cpSaving ? 'Saving…' : 'Save'}
                  </button>
                )}
                <button onClick={() => setShowControlPlan(false)}
                  style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                    borderRadius:6, padding:'8px 16px', fontSize:13, cursor:'pointer' }}>
                  {canManage ? 'Cancel' : 'Close'}
                </button>
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

// ── BOM split-view panel ───────────────────────────────────────────────────────

function BomPanel({ bomItems, stations, flow, onClose }) {
  const usedNumbers = new Set();
  (stations || []).forEach(st => (st.parts || []).forEach(p => { if (p.partNumber) usedNumbers.add(p.partNumber); }));
  const usedCount = (bomItems || []).filter(item => usedNumbers.has(item.ninPartNumber)).length;
  const total = (bomItems || []).length;

  const tag = (used) => (
    <span style={{ display:'inline-block', padding:'1px 8px', borderRadius:20, fontSize:10, fontWeight:700,
      letterSpacing:'0.05em', textTransform:'uppercase',
      background: used ? 'rgba(70,187,137,0.18)' : 'rgba(148,163,184,0.15)',
      color: used ? 'var(--ni-green)' : '#94a3b8' }}>
      {used ? 'Used' : 'Unused'}
    </span>
  );

  return (
    <div style={{ width:340, borderLeft:'1px solid var(--ni-border-soft)',
      background:'var(--ni-char)', overflowY:'auto', display:'flex', flexDirection:'column', flexShrink:0 }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--ni-border-soft)',
        display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ width:10, height:10, borderRadius:3, background:BOM_ACCENT, flexShrink:0 }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:14, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            {flow.bomName || 'Bill of Materials'}
          </div>
          <div style={{ fontSize:11, color:'var(--ni-text-muted)', fontFamily:'monospace' }}>
            {flow.bomDocumentNumber || ''}
          </div>
        </div>
        <button onClick={onClose}
          style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:16 }}>×</button>
      </div>

      <div style={{ padding:'8px 16px', borderBottom:'1px solid var(--ni-border-soft)',
        fontSize:12, color:'var(--ni-text-muted)' }}>
        <span style={{ color:'var(--ni-green)', fontWeight:700 }}>{usedCount}</span> of {total} items used
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:8 }}>
        {!total && (
          <div style={{ fontSize:12, color:'var(--ni-text-muted)', padding:'8px 8px' }}>No BOM items.</div>
        )}
        {(bomItems || []).map((item, i) => {
          const used = usedNumbers.has(item.ninPartNumber);
          return (
            <div key={item.ninPartNumber || i}
              style={{ padding:'7px 8px', borderRadius:6, marginBottom:4,
                background: used ? 'rgba(70,187,137,0.07)' : 'var(--ni-panel)',
                border: `1px solid ${used ? 'rgba(70,187,137,0.25)' : 'var(--ni-border-soft)'}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'center' }}>
                <span style={{ fontSize:12, color:BOM_ACCENT, fontFamily:'monospace',
                  whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {item.ninPartNumber || '—'}
                </span>
                {tag(used)}
              </div>
              {item.description && (
                <div style={{ fontSize:11, color:'var(--ni-text-muted)', marginTop:3 }}>{item.description}</div>
              )}
              <div style={{ fontSize:10, color:'var(--ni-text-muted)', marginTop:2, fontFamily:'monospace' }}>
                {item.quantity ?? 1} {item.unit || 'pcs'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--ni-char)', border:'1px solid var(--ni-border-soft)', borderRadius:12,
        padding:24, minWidth:360, maxWidth:480, width:'90%', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <span style={{ fontWeight:700, fontSize:16 }}>{title}</span>
          <button aria-label={`Close ${title}`} onClick={onClose} style={{ background:'none', border:'none', color:'var(--ni-text-muted)',
            cursor:'pointer', fontSize:18 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
